import { useEffect, useRef } from 'react';

/**
 * Fires `startSearch` on mount and whenever it changes, de-duplicated so a
 * single page load makes a single POST. Two things cause redundant fires:
 * StrictMode's dev setup → cleanup → setup double-invoke, and sessionStorage
 * rehydration mounting with the previous society's settings (which gives
 * `startSearch` a fresh identity even when the request variables are unchanged).
 *
 * Pass `signature` (stable JSON of the real request variables) to de-dupe on
 * content rather than identity, collapsing both cases to one request while
 * still re-firing on genuine filter/sort/pagination changes. Without it,
 * de-dupes by `startSearch` identity only (covers StrictMode).
 */
export function useStartSearchOnMount(
  startSearch: () => void,
  shouldRun: boolean = true,
  signature?: string,
) {
  const lastFiredRef = useRef<typeof startSearch | null>(null);
  const lastFiredSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldRun) {
      // Clearing a search starts a new cycle, even if the next criteria match.
      lastFiredRef.current = null;
      lastFiredSignatureRef.current = null;
      return;
    }

    if (signature !== undefined) {
      if (lastFiredSignatureRef.current === signature) return;
      lastFiredSignatureRef.current = signature;
      startSearch();
      return;
    }

    if (lastFiredRef.current === startSearch) return;
    lastFiredRef.current = startSearch;
    startSearch();
  }, [shouldRun, startSearch, signature]);
}
